/**
 * HUB-A — Filtres Centre Activité synchronisés avec l'URL.
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ACTIVITE_TAB_IDS, resolveActiviteTab, getVisibleActiviteTabs } from '../../utils/activite/activityTabs';

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function daysAgoISO(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

export default function useActiviteFilters({ isAdmin, isCarlosOnly }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const visibleTabs = useMemo(
    () => getVisibleActiviteTabs({ isAdmin, isCarlosOnly }),
    [isAdmin, isCarlosOnly],
  );

  const tab = resolveActiviteTab(searchParams.get('tab'), visibleTabs);

  const filters = useMemo(() => ({
    search: searchParams.get('search') || '',
    magasin_id: searchParams.get('magasin_id') || '',
    vendeur: searchParams.get('vendeur') || '',
    date_debut: searchParams.get('date_debut') || '',
    date_fin: searchParams.get('date_fin') || '',
    period: searchParams.get('period') || '',
  }), [searchParams]);

  const setParams = useCallback((patch) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(patch).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') {
          next.delete(key);
        } else {
          next.set(key, String(value));
        }
      });
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const setTab = useCallback((tabId) => {
    setParams({ tab: tabId });
  }, [setParams]);

  const setSearch = useCallback((search) => {
    setParams({ search: search || null });
  }, [setParams]);

  const setMagasinId = useCallback((magasin_id) => {
    setParams({ magasin_id: magasin_id || null });
  }, [setParams]);

  const setVendeur = useCallback((vendeur) => {
    setParams({ vendeur: vendeur || null });
  }, [setParams]);

  const setPeriod = useCallback((period) => {
    if (period === 'today') {
      const t = todayISO();
      setParams({ period: 'today', date_debut: t, date_fin: t });
    } else if (period === '7d') {
      setParams({ period: '7d', date_debut: daysAgoISO(6), date_fin: todayISO() });
    } else if (period === '30d') {
      setParams({ period: '30d', date_debut: daysAgoISO(29), date_fin: todayISO() });
    } else {
      setParams({ period: null, date_debut: null, date_fin: null });
    }
  }, [setParams]);

  const setDateRange = useCallback((date_debut, date_fin) => {
    setParams({
      period: date_debut || date_fin ? 'custom' : null,
      date_debut: date_debut || null,
      date_fin: date_fin || null,
    });
  }, [setParams]);

  const defaultTab = visibleTabs[0]?.id || ACTIVITE_TAB_IDS.OVERVIEW;

  return {
    tab,
    filters,
    visibleTabs,
    defaultTab,
    setTab,
    setSearch,
    setMagasinId,
    setVendeur,
    setPeriod,
    setDateRange,
  };
}
